import React, { createContext, useContext, useMemo } from 'react';
import type { TenantConfig } from '@alterrego/core-config';
import { tokens } from './tokens';

type Theme = Omit<typeof tokens, 'color' | 'font'> & {
  color: { [K in keyof typeof tokens.color]: string };
  font: { family: string; size: typeof tokens.font.size; weight: typeof tokens.font.weight };
  tenant: TenantConfig;
};

const ThemeContext = createContext<Theme | null>(null);

export function ThemeProvider({
  config,
  children,
}: {
  config: TenantConfig;
  children: React.ReactNode;
}) {
  const theme = useMemo<Theme>(() => {
    const merged = {
      ...tokens,
      color: {
        ...tokens.color,
        primary: config.branding.primary,
        primaryDark: config.branding.secondary,
        ink: config.branding.dark,
      },
      font: {
        ...tokens.font,
        family: `"${config.branding.fontFamily}", -apple-system, system-ui, sans-serif`,
      },
      tenant: config,
    };

    // Injecte les CSS variables au niveau :root pour usage dans les styles
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.style.setProperty('--color-primary', merged.color.primary);
      root.style.setProperty('--color-primary-dark', merged.color.primaryDark);
      root.style.setProperty('--color-ink', merged.color.ink);
      root.style.setProperty('--font-family', merged.font.family);
    }

    return merged;
  }, [config]);

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Theme {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme doit être utilisé dans un <ThemeProvider>');
  return ctx;
}
